<script lang="ts">import { Dropdown } from "@infinitro/dropdown";
import Input from "./input.svelte";
import { buildCountryOptions, findConfigByOptionValue, formatMatchedRegionLabel, getDefaultCountryOption, normalizeNationalNumber, parseOptionValue, validatePhoneAgainstConfig } from "./phone-utils";
let { id = "phone-input", config = [], telCountryCode = $bindable(""), nationalNumber = $bindable(""), country = $bindable(""), valid = $bindable(false), matchedCountryName = $bindable(""), placeholder = "", disabled = false, loading = false, showError = false, errorMessage = "", size = "large", customClasses = "", inputClasses = "", customBorderRadius = undefined, prefixDropdownClasses = "phone-prefix-dropdown", prefixDropdownInputClasses = "", onInput, onBlur } = $props();
let selectedOption = $state("");
const countryOptions = $derived(buildCountryOptions(config));
const prefixPlaceholder = $derived(countryOptions[0]?.label ?? "+");
const selectedConfig = $derived(findConfigByOptionValue(config, selectedOption));
const preferredCountry = $derived(selectedConfig?.country || country || undefined);
$effect.pre(() => {
	if (config.length === 0) return;
	const stillValid = selectedOption && findConfigByOptionValue(config, selectedOption);
	if (!stillValid) {
		if (telCountryCode) {
			const matchForCode = countryOptions.find((option) => {
				const parsed = parseOptionValue(option.value);
				return parsed?.telCountryCode === String(telCountryCode);
			});
			selectedOption = matchForCode?.value ?? getDefaultCountryOption(config);
		} else {
			selectedOption = getDefaultCountryOption(config);
		}
	}
	const parsed = parseOptionValue(selectedOption);
	if (parsed) {
		if (telCountryCode !== parsed.telCountryCode) {
			telCountryCode = parsed.telCountryCode;
		}
		if (country !== parsed.country) {
			country = parsed.country;
		}
	}
});
const validation = $derived(config.length > 0 && telCountryCode ? validatePhoneAgainstConfig(config, telCountryCode, nationalNumber, preferredCountry) : {
	valid: false,
	e164: "",
	matchedConfig: null
});
// Keep bindable outputs in sync immediately (avoid stale true after shortening a number).
$effect.pre(() => {
	valid = validation.valid;
	matchedCountryName = validation.matchedConfig ? formatMatchedRegionLabel(validation.matchedConfig) : "";
});
const handleCountryChange = (value) => {
	selectedOption = value;
	const parsed = parseOptionValue(value);
	if (parsed) {
		telCountryCode = parsed.telCountryCode;
		country = parsed.country;
	}
	onInput?.();
};
const handleNationalInput = (value) => {
	nationalNumber = normalizeNationalNumber(value);
	onInput?.();
};
</script>

<div class="phone-input-row" class:phone-input-disabled={disabled || loading}>
	<div class="phone-prefix-field">
		<Dropdown
			bind:value={selectedOption}
			options={countryOptions}
			placeholder={prefixPlaceholder}
			{size}
			customClasses="phone-input-prefix {prefixDropdownClasses} {customClasses}"
			inputClasses={prefixDropdownInputClasses || inputClasses}
			enableSearch={countryOptions.length > 1}
			menuPosition="bottom"
			width="100%"
			disabled={disabled || loading || countryOptions.length === 0}
			onChange={handleCountryChange}
		/>
	</div>
	<div class="phone-number-field">
		<Input
			{id}
			bind:value={nationalNumber}
			{placeholder}
			inputmode="numeric"
			pattern="[0-9]*"
			error={showError}
			message={showError ? errorMessage : ''}
			{size}
			{customClasses}
			{inputClasses}
			{customBorderRadius}
			disabled={disabled || loading}
			onInput={handleNationalInput}
			{onBlur}
		/>
	</div>
</div>

<style>
	.phone-input-row {
		display: flex;
		width: 100%;
		min-width: 0px;
		align-items: flex-start;
		gap: 0.5rem;
}

	.phone-prefix-field {
		position: relative;
		z-index: 50;
		width: 11rem;
		min-width: 11rem;
		max-width: 45%;
		flex-shrink: 0;
}

	.phone-number-field {
		min-width: 0px;
		flex: 1 1 0%;
}

	.phone-input-disabled {
		opacity: 0.7;
}

	.phone-region-hint {
		margin-top: 0.25rem;
		display: block;
		font-size: 0.75rem;
		line-height: 1rem;
		opacity: 0.7;
}

	:global(.phone-input-prefix) {
		width: 100% !important;
		min-width: 0px !important;
		max-width: 100% !important;
}

	:global(.phone-input-prefix .input > span) {
		display: block;
		min-width: 0px;
		flex: 1 1 0%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
}

	/* Menu can be wider than the closed field so long country names stay readable.
	   Use opaque tokens — background-500 is translucent on some brands (e.g. turboslots). */
	:global(.phone-input-prefix .options-list),
	:global(.phone-input-prefix .options-list.options-list-blur),
	:global(.phone-input-prefix .options-list.options-list-alternative2-glass) {
		min-width: 16rem;
		width: -moz-max-content;
		width: max-content;
		max-width: min(20rem, 80vw);
		z-index: 50;
		background: var(--color-background-100) !important;
		background-image: none !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
		opacity: 1 !important;
	}

	:global(.phone-input-prefix .options-list .search-input) {
		background: var(--color-background-300) !important;
		background-image: none !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
		opacity: 1 !important;
	}

	:global(.phone-input-prefix .options-list .search-container),
	:global(.phone-input-prefix .options-list .search-container-blur) {
		background: transparent !important;
		backdrop-filter: none !important;
		-webkit-backdrop-filter: none !important;
	}
</style>
